import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { ClipboardList, LucideAngularModule, Pencil, Plus, Search, Trash2 } from 'lucide-angular';
import { MenuItem } from '../../../core/models/domain.models';
import { ToastService } from '../../../shared/ui/toast.service';
import { MenuManagementService } from '../services/menu-management.service';

interface MenuForm {
  menuName: string;
  menuUrl: string;
  userType: string;
  menuPriority: number;
  menuList: string;
  parentId: string;
  menuAlias: string;
  menuIcon: string;
  sendImage: string;
  isDefault: boolean;
  isShow: boolean;
}

@Component({
  standalone: true,
  imports: [CommonModule, LucideAngularModule],
  templateUrl: './menu-management.page.html',
  styleUrl: './menu-management.page.scss',
})
export class MenuManagementPage {
  private readonly service = inject(MenuManagementService);
  private readonly toast = inject(ToastService);

  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly SearchIcon = Search;
  protected readonly PlusIcon = Plus;
  protected readonly PencilIcon = Pencil;
  protected readonly Trash2Icon = Trash2;

  protected readonly search = signal('');
  protected readonly rows = signal<MenuItem[]>([]);
  protected readonly showForm = signal(false);
  protected readonly editingId = signal<string | null>(null);
  protected readonly formError = signal('');
  protected readonly submitting = signal(false);
  protected readonly form = signal<MenuForm>(this.emptyForm());

  protected readonly filteredRows = computed(() => {
    const term = this.search().trim().toLowerCase();
    if (!term) return this.rows();
    return this.rows().filter((row) =>
      [
        row.menu_name,
        row.user_type,
        row.menu_url,
        row.menu_list,
        this.parentLabel(row),
        row.menu_alias || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  });
  protected readonly parentOptions = computed(() => {
    const editingId = this.editingId();
    const blocked = editingId ? this.descendantIds(editingId) : new Set<string>();
    const candidates = this.rows()
      .filter((row) => row.id !== editingId && !blocked.has(row.id))
      .map((row) => ({
        id: row.id,
        label: this.menuPath(row.id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return candidates;
  });

  constructor() {
    this.load();
  }

  protected onSearch(value: string): void {
    this.search.set(value);
  }

  protected openAdd(): void {
    this.editingId.set(null);
    this.formError.set('');
    this.form.set(this.emptyForm());
    this.showForm.set(true);
  }

  protected openEdit(row: MenuItem): void {
    this.editingId.set(row.id);
    this.formError.set('');
    this.form.set({
      menuName: row.menu_name || '',
      menuUrl: row.menu_url || '',
      userType: row.user_type || '',
      menuPriority: Number(row.menu_priority || 0),
      menuList: row.menu_list || '',
      parentId: row.parent_id || '',
      menuAlias: row.menu_alias || '',
      menuIcon: row.menu_icon || '',
      sendImage: row.send_image || '',
      isDefault: !!row.is_default,
      isShow: row.is_show !== false,
    });
    this.showForm.set(true);
  }

  protected closeForm(): void {
    this.showForm.set(false);
    this.editingId.set(null);
    this.submitting.set(false);
  }

  protected setField<K extends keyof MenuForm>(key: K, value: MenuForm[K]): void {
    this.form.update((current) => ({ ...current, [key]: value }));
  }

  protected save(): void {
    if (this.submitting()) return;
    const raw = this.form();
    const menuName = raw.menuName.trim();
    const menuUrl = raw.menuUrl.trim();
    const userType = raw.userType.trim();
    const menuList = raw.menuList.trim();

    if (!menuName || !menuUrl || !userType || !menuList) {
      this.formError.set('Menu Name, Menu URL, User Type and Menu List are required.');
      return;
    }
    if (!Number.isFinite(raw.menuPriority)) {
      this.formError.set('Menu priority must be a valid number.');
      return;
    }

    this.formError.set('');
    this.submitting.set(true);

    const payload: Partial<MenuItem> = {
      menu_name: menuName,
      menu_url: menuUrl,
      user_type: userType,
      menu_priority: Number(raw.menuPriority),
      menu_list: menuList,
      parent_id: raw.parentId || null,
      menu_alias: raw.menuAlias.trim() || null,
      menu_icon: raw.menuIcon.trim() || null,
      send_image: raw.sendImage.trim() || raw.menuIcon.trim() || null,
      is_default: !!raw.isDefault,
      is_show: !!raw.isShow,
      active: true,
    };

    const request = this.editingId() ? this.service.update(this.editingId()!, payload) : this.service.create(payload);
    request.subscribe({
      next: () => {
        this.submitting.set(false);
        this.showForm.set(false);
        this.toast.show(this.editingId() ? 'Menu updated' : 'Menu created', '', 'success');
        this.load();
      },
      error: () => {
        this.submitting.set(false);
        this.formError.set('Failed to save menu.');
      },
    });
  }

  protected remove(row: MenuItem): void {
    if (!confirm(`Delete menu "${row.menu_name}"?`)) return;
    this.service.delete(row.id).subscribe({
      next: () => {
        this.toast.show('Menu deleted', '', 'success');
        this.load();
      },
      error: () => this.toast.show('Failed to delete menu', '', 'danger'),
    });
  }

  private load(): void {
    this.service.list().subscribe({
      next: (rows) => this.rows.set(rows),
      error: () => this.rows.set([]),
    });
  }

  protected rowLevel(row: MenuItem): number {
    if (typeof row.level === 'number') return Math.max(0, row.level);
    return this.calculateLevel(row.id);
  }

  protected parentLabel(row: MenuItem): string {
    if (!row.parent_id) return '-';
    return this.menuPath(row.parent_id);
  }

  protected menuPath(menuId: string): string {
    const byId = new Map(this.rows().map((row) => [row.id, row]));
    const names: string[] = [];
    const seen = new Set<string>();
    let current = byId.get(menuId);
    while (current && !seen.has(current.id)) {
      seen.add(current.id);
      names.push(current.menu_name);
      current = current.parent_id ? byId.get(current.parent_id) : undefined;
    }
    return names.reverse().join(' > ');
  }

  private calculateLevel(menuId: string): number {
    const byId = new Map(this.rows().map((row) => [row.id, row]));
    let level = 0;
    const seen = new Set<string>();
    let current = byId.get(menuId);
    while (current?.parent_id && !seen.has(current.parent_id)) {
      seen.add(current.parent_id);
      const parent = byId.get(current.parent_id);
      if (!parent) break;
      level += 1;
      current = parent;
    }
    return level;
  }

  private descendantIds(rootId: string): Set<string> {
    const childrenByParent = new Map<string, string[]>();
    for (const row of this.rows()) {
      if (!row.parent_id) continue;
      const current = childrenByParent.get(row.parent_id) || [];
      current.push(row.id);
      childrenByParent.set(row.parent_id, current);
    }
    const descendants = new Set<string>();
    const queue = [...(childrenByParent.get(rootId) || [])];
    while (queue.length > 0) {
      const next = queue.shift()!;
      if (descendants.has(next)) continue;
      descendants.add(next);
      queue.push(...(childrenByParent.get(next) || []));
    }
    return descendants;
  }

  private emptyForm(): MenuForm {
    return {
      menuName: '',
      menuUrl: '',
      userType: '',
      menuPriority: 0,
      menuList: '',
      parentId: '',
      menuAlias: '',
      menuIcon: '',
      sendImage: '',
      isDefault: false,
      isShow: true,
    };
  }
}
